<template>
  <div class="container-fluid session">
    <!-- Page title -->
    <page-title
      :isDataLoading="isLoading"
      :title="this.$t('navbar.tab.session')"
      iconClasses="fas fa-tablet-alt"
      :isTimelineDisplayed="false"
    />

    <!-- Header with session's informations -->
    <div class="container-fluid" v-if="downloaded">
      <div class="row justify-content-md-center">
        <div class="col-auto">
          <span>
            {{ $t('navbar.tab.session') }} :
            <strong>{{ session }}</strong>
          </span>
          <br />
          <span>
            {{ $t('device') }} :
            <strong>
              <router-link :to="'/sessions/device/' + dataset.device_serial">{{
                dataset.device_serial
              }}</router-link>
            </strong>
          </span>
          <br />
        </div>
        <div class="col-auto">
          <span>
            {{ $t('filter.site') }} :
            <strong>{{ getLocalizedSiteName(dataset.raw_log.site) }}</strong>
          </span>
          <br />
          <span>
            {{ $t('filter.language') }} :
            <strong>{{ $t('language.' + dataset.raw_log.language) }}</strong>
          </span>
          <br />
          <span>
            {{ $t('session.duration') }} :
            <strong>{{ visitDurationLabel }}</strong>
          </span>
          <br />
        </div>
        <div class="col-auto">
          <span>
            {{ $t('start') }} :
            <strong>{{ getTimeLabel('start_time') }}</strong>
          </span>
          <br />
          <span>
            {{ $t('end') }} :
            <strong>{{ getTimeLabel('end_time') }}</strong>
          </span>
          <br />
          <span v-if="dataset.raw_log.uploadedTime">
            {{ $t('upload') }} :
            <strong>{{ getTimeLabel('uploaded_time') }}</strong>
          </span>
        </div>
      </div>
    </div>

    <div class="row" v-html="'&nbsp;'"></div>

    <!-- Tabs contents -->
    <nav>
      <div class="nav nav-tabs justify-content-center" id="nav-tab" role="tablist">
        <a
          class="nav-item nav-link active"
          id="nav-timeline-tab"
          data-toggle="tab"
          href="#nav-timeline"
          role="tab"
          aria-controls="nav-timeline"
          aria-selected="true"
          >Timeline</a
        >
        <a
          v-if="hasMap"
          class="nav-item nav-link"
          id="nav-map-tab"
          data-toggle="tab"
          href="#nav-map"
          role="tab"
          aria-controls="nav-map"
          aria-selected="false"
          @click.once="onMapShow()"
          >{{ $t('maps') }}</a
        >
        <a
          v-if="hasSurvey"
          class="nav-item nav-link"
          id="nav-survey-tab"
          data-toggle="tab"
          href="#nav-survey"
          role="tab"
          aria-controls="nav-survey"
          aria-selected="false"
          >{{ $t('survey') }}</a
        >
        <a
          v-if="hasWarnings"
          class="nav-item nav-link"
          id="nav-warnings-tab"
          data-toggle="tab"
          href="#nav-warnings"
          role="tab"
          aria-controls="nav-warnings"
          aria-selected="false"
          >{{ $t('warnings') }}</a
        >
      </div>
    </nav>

    <!-- Session's timeline -->
    <div class="tab-content mb-3" id="nav-tabContent">
      <div
        class="tab-pane fade show active"
        id="nav-timeline"
        role="tabpanel"
        aria-labelledby="nav-timeline-tab"
      >
        <div class="container-fluid">
          <div class="row">&nbsp;</div>
          <div class="row">
            <div class="col">
              <!-- Timeline -->
              <div :id="chartId" class="container-chart"></div>
            </div>
          </div>

          <!-- Button with collapser to display readable log events -->
          <div
            class="row justify-content-md-center mb-3"
            v-if="dataset && dataset.raw_log && dataset.raw_log.logVersionMajor >= 4"
          >
            <button
              v-if="dataset && dataset.raw_log"
              class="btn btn-dark btn-very-sm"
              type="button"
              data-toggle="collapse"
              data-target="#collapseLogReader"
              aria-expanded="false"
              aria-controls="collapseLogReader"
              v-tooltip="{
                content: 'Read log',
                placement: 'top-center',
              }"
            >
              <i class="fas fa-search mr-1"></i><i class="fas fa-file-alt"></i>
            </button>
          </div>

          <div
            class="collapse"
            id="collapseLogReader"
            v-if="dataset && dataset.raw_log && dataset.raw_log.logVersionMajor >= 4"
          >
            <!-- Log reader component -->
            <log-reader
              v-bind:session="dataset.raw_log"
              v-bind:selectedLogEvent="selectedLogEvent"
              @overLogEvent="onOverLogEvent"
              @outLogEvent="onOutLogEvent"
            ></log-reader>
          </div>
          <div class="row">
            <div class="col">
              <div class="row justify-content-center">
                <span class="title-font" v-if="downloaded">{{ $t('battery.level') }}</span>
              </div>
              <div :id="batteryLevelChartId" class="container-chart"></div>
            </div>
          </div>
          <div class="row mt-2">
            <div class="col">
              <div class="row justify-content-center">
                <span class="title-font" v-if="downloaded">{{ $t('battery.temperature') }}</span>
              </div>
              <div :id="batteryTemperatureChartId" class="container-chart"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Session's map -->
      <div
        class="tab-pane fade"
        id="nav-map"
        role="tabpanel"
        aria-labelledby="nav-map-tab"
        v-if="routeForm && hasMap"
      >
        <div class="container-fluid">
          <div class="row justify-content-md-center pt-2">
            <div :class="formElementClass">
              <label for="inputRouteDataType" class="small" v-html="'&nbsp;'"></label>
              <select
                id="inputRouteDataType"
                class="form-control"
                v-model="routeForm.dataType"
                @change="loadMapData"
              >
                <option value="duration">{{ $t('chart.data_type.duration') }}</option>
                <option value="count">{{ $t('chart.data_type.passages_count') }}</option>
                <option value="beacon_detected">{{ $t('chart.data_type.beacon_detected') }}</option>
              </select>
            </div>
            <div :class="formElementClass">
              <label for="inputRouteDataType" class="small">{{ $t('at') }}</label>
              <select
                id="inputLevel"
                class="form-control"
                v-model="routeForm.level"
                @change="loadMapData"
                v-if="siteLevels && siteLevels.levels"
              >
                <option :value="level" v-for="level in siteLevels.levels" :key="level">
                  {{ $t('level') }} {{ level }}
                </option>
              </select>
            </div>
          </div>
          <div
            class="row justify-content-md-center mt-2"
            v-if="dataset && dataset.raw_log.site !== 'france.paris.pcv'"
          >
            <svg-map
              v-bind:dataset="dataset"
              :routeForm="routeForm"
              :startDate="startDate"
              :endDate="endDate"
              :colorSpectrum="selectedColorSpectrum"
            ></svg-map>
          </div>

          <open-street-map
            v-if="dataset && dataset.raw_log.site === 'france.paris.pcv'"
            :dataset="dataset"
            :routeForm="routeForm"
            :startDate="startDate"
            :endDate="endDate"
          ></open-street-map>
        </div>
      </div>

      <!-- Sessions's survey -->
      <div
        v-if="hasSurvey"
        class="tab-pane fade"
        id="nav-survey"
        role="tabpanel"
        aria-labelledby="nav-survey-tab"
      >
        <div class="container-fluid pt-3 pb-3">
          <div
            class="row justify-content-md-center m-1"
            v-for="answer in dataset.survey_answers"
            :key="answer.question_index"
          >
            <span class="center-title" v-if="answer.answer_type == 'Rating' && answer.answer">
              <strong
                >{{ answer.question_index }}.
                {{ $t('survey_questions.' + answer.question) }}</strong
              >
              : {{ answer.answer }}
              <!--<i class="fas fa-star" v-for="index in parseInt(answer.answer)" :key="index"></i>-->
            </span>
            <span class="center-title" v-if="answer.answer_type == 'Choice'">
              <strong>{{ answer.question_index }}.</strong>
              <strong>{{ $t('survey_questions.' + answer.question) }}</strong>
              :
              {{
                isSurveyOriginQuestion(answer.question)
                  ? answer.answer
                  : $t('survey_answers.' + answer.answer)
              }}
            </span>
            <span class="center-title" v-if="answer.answer_type == 'Slider'">
              <strong>{{ answer.question_index }}.</strong>
              <strong>{{ $t('survey_questions.' + answer.question) }}</strong>
              : {{ answer.answer }}
            </span>
          </div>
        </div>
      </div>
      <div
        class="tab-pane fade"
        id="nav-warnings"
        role="tabpanel"
        aria-labelledby="nav-warnings-tab"
        v-if="hasWarnings"
      >
        <div class="container-fluid p-3">
          <div
            class="row justify-content-md-center m-1"
            v-for="(warning, index) in dataset.warnings"
            :key="index"
          >
            <span class="center-title" v>
              <strong>{{ eventTime(warning.start_time) }} : </strong> &nbsp;
              <strong>{{ warning.name }}</strong>
              -
              {{ areaName(warning.area) }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <div class="container-fluid mt-2">
      <div class="row" v-if="downloaded">
        <button class="btn btn-chart mb-3 mx-auto" @click="downloadSession">
          {{ $t('download.session') }}
          <i class="ml-2 fas fa-download"></i>
        </button>
      </div>
    </div>
    <a id="downloadAnchorElem" style="display: none"></a>
  </div>
</template>

<script>
/* eslint-disable */
import moment from 'moment-timezone'
import { Chart } from '../utils/chart'
import * as d3 from 'd3'
import d3Tip from 'd3-tip'
import Rainbow from 'rainbowvis.js'
import { utilsMixin, sitesMixin } from '../utils/mixins'
import { setTimeout } from 'timers'

export default {
  name: 'Session',
  dependencies: ['dataService', 'adminService'],
  mixins: [utilsMixin, sitesMixin],
  data() {
    return {
      downloaded: false,
      isLoading: false,
      dataset: {},
      mapDataset: {},
      chartId: 'sessionTimelineChart',
      batteryLevelChartId: 'sessionBatteryLevelChart',
      batteryTemperatureChartId: 'sessionBatteryTemperatureChart',
      width: 1080,
      height: 75,
      displayMap: false,
      chart: null,
      batteryLevelChart: null,
      batteryTemperatureChart: null,
      batteryLevelPath: null,
      batteryTemperaturePath: null,
      tooltip: null,
      session: this.$route.params.session,
      siteID: this.$route.params.site,
      zoom: null,
      timelineXAxis: null,
      lines: [],
      startDate:
        this.$route.params.start != undefined
          ? moment(this.$route.params.start, 'YYYYY-MM-DD')
          : moment().subtract(1, 'month'),
      endDate:
        this.$route.params.end != undefined
          ? moment(this.$route.params.end, 'YYYYY-MM-DD')
          : moment(),
      batteryCharts: [],
      sessionLegendColors: [
        {
          alt1: '#FE6786',
          alt2: '#ff9eb1',
        },
        {
          alt1: '#FF7BC4',
          alt2: '#ffadda',
        },
        {
          alt1: '#FAE83C',
          alt2: '#FAE83C',
        },
        {
          alt1: '#FFA064',
          alt2: '#FFA064',
        },
        {
          alt1: '#B066FE',
          alt2: '#B066FE',
        },
        {
          alt1: '#5EDCE7',
          alt2: '#5EDCE7',
        },
        {
          alt1: '#9BF05E',
          alt2: '#9BF05E',
        },
        {
          alt1: '#C89EFD',
          alt2: '#C89EFD',
        },
        {
          alt1: '#FA0000',
          alt2: '#FA0000',
        },
        {
          alt1: '#FFFDB9',
          alt2: '#FFFDB9',
        },
        {
          alt1: '#66a3ff',
          alt2: '#66a3ff',
        },
      ],
      defaultSiteLevels: { levels: [0, 1], default_level: 0 },
      d3Map: null,
      canvasWidth: 1280,
      canvasHeight: 720,
      colorRainbow: new Rainbow(),
      levelsSVG: [],
      routeForm: null,
      selectedLogEvent: null,
      clickedLogEvent: null,
      logReaderCollapsed: true,
      colorSpectrum: {
        default: ['#FFF858', '#FF6B6B'],
        beacon_detected: ['#b3ff66'],
      },
    }
  },
  mounted() {
    moment.tz.setDefault('UTC')
    d3.tip = d3Tip
    d3.selectAll('.d3-tooltip').remove()
    this.colorRainbow.setSpectrum('#FFF858', '#FF6B6B')

    let that = this

    this.chart = new Chart('sessionTimeline', this.sessionTest, 'session', this.width, this.height)
    this.chart.margin.bottom = 10

    $('#collapseLogReader').on('shown.bs.collapse', () => {
      if (this.clickedLogEvent) {
        this.selectedLogEvent = this.clickedLogEvent
      }
      this.logReaderCollapsed = false
    })

    $('#collapseLogReader').on('hidden.bs.collapse', () => {
      this.clickedLogEvent = null
      this.logReaderCollapsed = true
    })

    this.tooltip = d3
      .tip()
      .attr('class', 'd3-tip')
      .html(function(d) {
        var onOverElement = d3.select(this)
        onOverElement.attr('fill', '#FFFFFF')
        let tooltipLabel = ''
        let areaName = d.area
        if (that.dataset.raw_log.logVersionMajor >= 4) {
          if (that.$i18n.messages[that.$i18n.locale]['map_areas'][that.dataset.raw_log.siteID]) {
            areaName =
              that.$i18n.messages[that.$i18n.locale]['map_areas'][that.dataset.raw_log.siteID][
                d.area
              ]
          }
        }

        const time = moment.utc(d.start * 1000).format('HH:mm:ss')

        tooltipLabel += time + ' : '

        if (d.type === 'Beacon' || d.type === 'Scan') {
          tooltipLabel += that.$t(`chart.data_value.${d.type.toLowerCase()}`) + ' - '
        }

        if (d.type === 'Warning' || d.type === 'Beacon') {
          tooltipLabel += `<strong>${d.name}</strong> - ${areaName}`
        } else if (d.type === 'Area') {
          tooltipLabel += `<strong>${areaName}</strong>`
        } else {
          tooltipLabel += `<strong>${d.name}</strong>`
        }

        return tooltipLabel
      })

    this.isLoading = true

    this.fetchSessionTimeline()
  },
  beforeDestroy() {
    this.tooltip.hide
  },
  computed: {
    mapContainerClass: function() {
      return this.fullScreen
        ? {}
        : {
            'p-3': true,
            'col-sm-7': this.isVeryLargeScreen,
            'col-sm-10': (this.isMobile && !this.isSmartPhone) || this.isLargeScreen,
          }
    },
    siteLevels: function() {
      let site = this.sitesWithMap.find(swm => {
        return String(swm.site) == String(this.dataset.raw_log.site)
      })
      if (site) {
        return site
      } else {
        return this.defaultSiteLevels
      }
    },
    hasMap: function() {
      return (
        this.dataset &&
        this.dataset.raw_log &&
        this.dataset.raw_log.logVersionMajor >= 4 &&
        this.dataset.raw_log.siteID != 13
      )
    },
    hasSurvey: function() {
      return this.dataset && this.dataset.survey_answers && this.dataset.survey_answers.length > 0
    },
    hasWarnings: function() {
      return this.dataset && this.dataset.warnings && this.dataset.warnings.length > 0
    },
    sessionLegends: function() {
      return [
        this.dataset.hotspots.length + ' ' + this.$t('chart.data_value.hotspot'),
        this.dataset.cartels.length + ' ' + this.$t('chart.data_value.cartel'),
        this.dataset.relics.length + ' ' + this.$t('chart.data_value.collected_treasure_count'),
        this.dataset.interactiveobjects.length +
          ' ' +
          this.$t('chart.data_value.interactiveobject'),
        this.dataset.interactivebuildings.length +
          ' ' +
          this.$t('chart.data_value.interactivebuilding'),
        this.dataset.immersives.length + ' ' + this.$t('chart.data_value.immersive'),
        this.dataset.augmentedvisits.length + ' ' + this.$t('chart.data_value.augmentedvisit'),
        this.dataset.areas.length + ' ' + this.$t('area'),
        this.dataset.warnings.length + ' ' + this.$t('chart.data_value.warning'),
        this.dataset.beacons.length + ' ' + this.$t('chart.data_value.beacon'),
        this.dataset.scans.length + ' ' + this.$t('chart.data_value.scan'),
      ]
    },
    visitDurationLabel: function() {
      let minutes = Math.round((this.dataset.end - this.dataset.start) / 60)
      return this.getVisitDurationLabel(minutes, 'minutes', minutes > 60 ? 'hh:mm:ss' : 'mm:ss')
    },
    selectedColorSpectrum: function() {
      if (this.colorSpectrum[this.routeForm.dataType]) {
        return this.colorSpectrum[this.routeForm.dataType]
      } else {
        return this.colorSpectrum.default
      }
    },
  },
  components: {
    'svg-map': () => import('./utils/SVGMap'),
    'open-street-map': () => import('./utils/OpenStreetMap'),
    'page-title': () => import('./utils/PageTitle'),
    'log-reader': () => import('./utils/LogReader'),
  },
  methods: {
    fetchSessionTimeline: function() {
      this.dataService
        .getSessionTimeline(this.session)
        .then(data => {
          this.downloaded = true
          this.dataset = data.result
          this.isLoading = false

          this.calculatePath()

          this.batteryCharts = []

          //Load and display battery level / temperature over session's duration
          if (this.dataset.battery_levels.length > 1) {
            this.calculateBatteryPath(this.dataset.battery_levels, true, this.batteryLevelChartId)
          }

          if (this.dataset.battery_temperatures.length > 1) {
            this.calculateBatteryPath(
              this.dataset.battery_temperatures,
              false,
              this.batteryTemperatureChartId,
            )
          }

          this.routeForm = {
            site: this.dataset.raw_log.site,
            level: this.dataset.raw_log.site === 'france.la-mongie.pic-du-midi' ? 4 : 0,
            dataType: 'duration',
            logVersionMajor: this.dataset.raw_log.logVersionMajor,
            logVersionMinor: this.dataset.raw_log.logVersionMinor,
            filters: {},
          }

          this.isLoading = false
        })
        .catch(error => {
          console.error('Error when retrieving session timeline:', error)
          this.isLoading = false
        })
    },
    calculatePath: function() {
      let that = this

      this.chart.initContainer(this.chartId, 'session')

      d3.selectAll('.d3-tip').style('opacity', 0)
      this.tooltip.hide
      this.tooltip.offset([-5, 0])
      this.chart.svg.call(this.tooltip)

      this.xScale = d3
        .scaleLinear()
        .range([0, this.chart.width])
        .domain([this.dataset.start, this.dataset.end])

      this.xReScale = this.xScale.copy()

      //When user zoom in the timeline
      this.zoom = d3
        .zoom()
        .scaleExtent([1, 8])
        .translateExtent([
          [0, 0],
          [this.chart.width, this.chart.height],
        ])
        .extent([
          [0, 0],
          [this.chart.width, this.chart.height],
        ])
        .on('zoom', function() {
          that.xReScale = d3.event.transform.rescaleX(that.xScale)

          that.timelineXAxis
            .transition()
            .duration(50)
            .call(that.chart.xAxis.scale(that.xReScale))

          that.batteryCharts.forEach(bchart => {
            if (!d3.select(bchart.idXAxisSelector).empty()) {
              d3.select(bchart.idXAxisSelector)
                .transition()
                .duration(50)
                .call(bchart.chart.xAxis.scale(that.xReScale))
            }
          })

          hotspotCircles.attr('cx', function(d) {
            return that.xReScale(moment(d.start))
          })

          cartelCircles.attr('cx', function(d) {
            return that.xReScale(moment(d.start))
          })

          relicCircles.attr('cx', function(d) {
            return that.xReScale(moment(d.start))
          })

          warningCircles.attr('cx', function(d) {
            return that.xReScale(moment(d.start))
          })

          /*scanCircles.attr('cx', function(d) {
            return that.xReScale(moment(d.start))
          })*/

          beaconShapes.attr('transform', function(d) {
            const el = d3.select(this)
            return `translate(${that.xReScale(d.start) - 4}, ${el.attr('y')})`
          })

          scanShapes.attr('transform', function(d) {
            const el = d3.select(this)
            return `translate(${that.xReScale(d.start)}, ${el.attr('y')})`
          })

          ioRects
            .attr('width', function(d) {
              return that.xReScale(moment(d.end)) - that.xReScale(moment(d.start))
            })
            .attr('transform', function(d) {
              return 'translate(' + that.xReScale(moment(d.start)) + ', 0)'
            })

          ibRects
            .attr('width', function(d) {
              return that.xReScale(moment(d.end)) - that.xReScale(moment(d.start))
            })
            .attr('transform', function(d) {
              return 'translate(' + that.xReScale(moment(d.start)) + ', 0)'
            })

          immersiveRects
            .attr('width', function(d) {
              return that.xReScale(moment(d.end)) - that.xReScale(moment(d.start))
            })
            .attr('transform', function(d) {
              return 'translate(' + that.xReScale(moment(d.start)) + ', 0)'
            })

          vaRects
            .attr('width', function(d) {
              return that.xReScale(moment(d.end)) - that.xReScale(moment(d.start))
            })
            .attr('transform', function(d) {
              return 'translate(' + that.xReScale(moment(d.start)) + ', 0)'
            })

          areaRects
            .attr('width', function(d) {
              return that.xReScale(moment(d.end)) - that.xReScale(moment(d.start))
            })
            .attr('transform', function(d) {
              return 'translate(' + that.xReScale(moment(d.start)) + ', 0)'
            })

          startLine
            .attr('x1', that.xReScale(moment(that.dataset.start)))
            .attr('x2', that.xReScale(moment(that.dataset.start)))

          endLine
            .attr('x1', that.xReScale(moment(that.dataset.end)))
            .attr('x2', that.xReScale(moment(that.dataset.end)))

          that.batteryCharts.forEach(bchart => {
            if (!d3.select(bchart.idPathSelector).empty()) {
              bchart.xScale = that.xReScale
              d3.select(bchart.idPathSelector).attr('d', bchart.valueline)
            }
          })
        })

      this.chart.svg.call(this.zoom)

      //Adding transparent rect to allow timeline grabbing
      this.chart.svg
        .append('rect')
        .attr('width', '100%')
        .attr('height', this.chart.height)
        .attr('fill', 'transparent')
        .attr('y', -30)
        .attr('style', 'cursor: grab;')
        .on('mousedown', function() {
          d3.selectAll('.tick')
            .selectAll('rect')
            .remove()
          d3.select(this).attr('style', 'cursor: grabbing;')
        })
        .on('mouseup', function() {
          d3.select(this).attr('style', 'cursor: grab;')
        })
        .on('mousemove', function() {
          d3.select(this).attr('style', 'cursor: grab;')
        })

      //Add start line
      const startLine = this.chart.svg
        .append('line')
        .attr('x1', this.xScale(this.dataset.start))
        .attr('x2', this.xScale(this.dataset.start))
        .attr('y1', 0)
        .attr('y2', 80)
        .style('stroke', '#FFF')
        .style('stroke-dasharray', (10, 10))

      //Add end line
      const endLine = this.chart.svg
        .append('line')
        .attr('x1', this.xScale(this.dataset.end))
        .attr('x2', this.xScale(this.dataset.end))
        .attr('y1', 0)
        .attr('y2', 80)
        .style('stroke', '#FFF')
        .style('stroke-dasharray', (10, 10))

      //Add clicked hotspots
      const hotspotCircles = this.chart.svg
        .selectAll('.hotspot')
        .data(this.dataset.hotspots)
        .enter()
        .append('circle')
        .attr('class', 'hotspot')
        .attr('fill', this.sessionLegendColors[0].alt1)
        .attr('r', 4)
        .attr('cx', function(d) {
          return that.xScale(d.start)
        })
        .attr('cy', function() {
          return -30
        })
        .on('mouseover', this.tooltip.show)
        .on('mouseout', function(d) {
          const onOverElement = d3.select(this)
          onOverElement.attr('fill', that.sessionLegendColors[0].alt1)
          that.tooltip.hide(d)
        })
        .on('click', function(d) {
          that.onEventClick(d)
        })

      //Add opened cartels
      const cartelCircles = this.chart.svg
        .selectAll('.cartel')
        .data(this.dataset.cartels)
        .enter()
        .append('circle')
        .attr('class', 'cartel')
        .attr('fill', this.sessionLegendColors[1].alt1)
        .attr('r', 4)
        .attr('cx', function(d) {
          return that.xScale(d.start)
        })
        .attr('cy', function() {
          return -20
        })
        .on('mouseover', this.tooltip.show)
        .on('mouseout', function(d) {
          var onOverElement = d3.select(this)
          onOverElement.attr('fill', that.sessionLegendColors[1].alt1)
          that.tooltip.hide(d)
        })
        .on('click', function(d) {
          that.onEventClick(d)
        })

      //Add found relics
      const relicCircles = this.chart.svg
        .selectAll('.relic')
        .data(this.dataset.relics)
        .enter()
        .append('circle')
        .attr('class', 'relic')
        .attr('fill', this.sessionLegendColors[2].alt1)
        .attr('r', 4)
        .attr('cx', function(d) {
          return that.xScale(d.start)
        })
        .attr('cy', function() {
          return -10
        })
        .on('mouseover', this.tooltip.show)
        .on('mouseout', function(d) {
          var onOverElement = d3.select(this)
          onOverElement.attr('fill', that.sessionLegendColors[2].alt1)
          that.tooltip.hide(d)
        })
        .on('click', function(d) {
          that.onEventClick(d)
        })

      let yOffset = 30

      //Add opened IO
      const ioRects = this.chart.svg
        .selectAll('.io')
        .data(this.dataset.interactiveobjects)
        .enter()
        .append('rect')
        .attr('class', 'io')
        .attr('fill', this.sessionLegendColors[3].alt1)
        .attr('y', yOffset)
        .attr('width', function(d) {
          return that.xScale(d.end) - that.xScale(d.start)
        })
        .attr('height', 15)
        .attr('transform', function(d) {
          return 'translate(' + that.xScale(d.start) + ', 0)'
        })
        .on('mouseover', this.tooltip.show)
        .on('mouseout', function(d) {
          var onOverElement = d3.select(this)
          onOverElement.attr('fill', that.sessionLegendColors[3].alt1)
          that.tooltip.hide(d)
        })
        .on('click', function(d) {
          that.onEventClick(d)
        })

      //Add opened IB
      const ibRects = this.chart.svg
        .selectAll('.ib')
        .data(this.dataset.interactivebuildings)
        .enter()
        .append('rect')
        .attr('class', 'ib')
        .attr('fill', this.sessionLegendColors[4].alt1)
        .attr('y', yOffset)
        .attr('width', function(d) {
          return that.xScale(d.end) - that.xScale(d.start)
        })
        .attr('height', 15)
        .attr('transform', function(d) {
          return 'translate(' + that.xScale(d.start) + ', 0)'
        })
        .on('mouseover', this.tooltip.show)
        .on('mouseout', function(d) {
          var onOverElement = d3.select(this)
          onOverElement.attr('fill', that.sessionLegendColors[4].alt1)
          that.tooltip.hide(d)
        })
        .on('click', function(d) {
          that.onEventClick(d)
        })

      if (
        (this.dataset.interactiveobjects && this.dataset.interactiveobjects.length > 0) ||
        (this.dataset.interactivebuildings && this.dataset.interactivebuildings.length > 0)
      ) {
        yOffset += 30
      }

      //Add opened immersives
      const immersiveRects = this.chart.svg
        .selectAll('.immersive')
        .data(this.dataset.immersives)
        .enter()
        .append('rect')
        .attr('class', 'immersive')
        .attr('fill', this.sessionLegendColors[5].alt1)
        .attr('y', yOffset)
        .attr('width', function(d) {
          return that.xScale(d.end) - that.xScale(d.start)
        })
        .attr('height', 15)
        .attr('transform', function(d) {
          return 'translate(' + that.xScale(d.start) + ', 0)'
        })
        .on('mouseover', this.tooltip.show)
        .on('mouseout', function(d) {
          const onOverElement = d3.select(this)
          onOverElement.attr('fill', that.sessionLegendColors[5].alt1)
          that.tooltip.hide(d)
        })
        .on('click', function(d) {
          that.onEventClick(d)
        })

      //Add opened VA
      const vaRects = this.chart.svg
        .selectAll('.va')
        .data(this.dataset.augmentedvisits)
        .enter()
        .append('rect')
        .attr('class', 'va')
        .attr('fill', this.sessionLegendColors[6].alt1)
        .attr('y', yOffset)
        .attr('width', function(d) {
          return that.xScale(d.end) - that.xScale(d.start)
        })
        .attr('height', 15)
        .attr('transform', function(d) {
          return 'translate(' + that.xScale(d.start) + ', 0)'
        })
        .on('mouseover', this.tooltip.show)
        .on('mouseout', function(d) {
          const onOverElement = d3.select(this)
          onOverElement.attr('fill', that.sessionLegendColors[6].alt1)
          that.tooltip.hide(d)
        })
        .on('click', function(d) {
          that.onEventClick(d)
        })

      if (
        (this.dataset.immersives && this.dataset.immersives.length > 0) ||
        (this.dataset.augmentedvisits && this.dataset.augmentedvisits.length > 0)
      ) {
        yOffset += 50
      }

      //Add opened areas
      const areaRects = this.chart.svg
        .selectAll('.area')
        .data(this.dataset.areas)
        .enter()
        .append('rect')
        .attr('class', 'area')
        .attr('fill', this.sessionLegendColors[7].alt1)
        .attr('y', yOffset)
        .attr('width', function(d) {
          return that.xScale(d.end) - that.xScale(d.start)
        })
        .attr('height', 15)
        .attr('transform', function(d) {
          return 'translate(' + that.xScale(d.start) + ', 0)'
        })
        .on('mouseover', this.tooltip.show)
        .on('mouseout', function(d) {
          const onOverElement = d3.select(this)
          onOverElement.attr('fill', that.sessionLegendColors[7].alt1)
          that.tooltip.hide(d)
        })
        .on('click', function(d) {
          that.onEventClick(d)
        })

      //Add triggered warning
      const warningCircles = this.chart.svg
        .selectAll('.warning')
        .data(this.dataset.warnings)
        .enter()
        .append('circle')
        .attr('class', 'warning')
        .attr('fill', this.sessionLegendColors[8].alt1)
        .attr('r', 4)
        .attr('cx', function(d) {
          return that.xScale(d.start)
        })
        .attr('cy', function() {
          return yOffset
        })
        .on('mouseover', this.tooltip.show)
        .on('mouseout', function(d) {
          const onOverElement = d3.select(this)
          onOverElement.attr('fill', that.sessionLegendColors[8].alt1)
          that.tooltip.hide(d)
        })
        .on('click', function(d) {
          that.onEventClick(d)
        })

      //Add selected beacons
      const beaconShapes = this.chart.svg
        .selectAll('.beacon')
        .data(this.dataset.beacons)
        .enter()
        .append('g')
        .attr('y', yOffset + 20)
        .attr('class', 'beacon')
        .attr('transform', function(d) {
          return `translate(${that.xScale(d.start) - 4}, ${yOffset + 20})`
        })
        .attr('fill', this.sessionLegendColors[9].alt1)
        .on('click', function(d) {
          that.onEventClick(d)
        })

      d3.selectAll('.beacon').each(function(d) {
        const el = d3.select(this)
        el.append('rect')
          .attr('width', 8)
          .attr('height', 8)
          .on('mouseover', that.tooltip.show)
          .on('mouseout', function(d) {
            const onOverElement = d3.select(this)
            onOverElement.attr('fill', that.sessionLegendColors[9].alt1)
            that.tooltip.hide(d)
          })

        el.append('line')
          .attr('x1', 4)
          .attr('y1', -8)
          .attr('x2', 4)
          .attr('y2', 0)
          .style('stroke-width', 1)
          .style('stroke', that.sessionLegendColors[9].alt1)
          .style('fill', 'none')
      })

      //Add validated scans
      const scanShapes = this.chart.svg
        .selectAll('.scan')
        .data(this.dataset.scans)
        .enter()
        .append('g')
        .attr('y', yOffset - 20)
        .attr('class', 'scan')
        .attr('transform', function(d) {
          return `translate(${that.xScale(d.start) - 4}, ${yOffset - 20})`
        })
        .attr('fill', this.sessionLegendColors[10].alt1)
        .on('click', function(d) {
          that.onEventClick(d)
        })

      d3.selectAll('.scan').each(function(d) {
        const el = d3.select(this)
        el.append('circle')
          .attr('r', 4)
          .on('mouseover', that.tooltip.show)
          .on('mouseout', function(d) {
            const onOverElement = d3.select(this)
            onOverElement.attr('fill', that.sessionLegendColors[10].alt1)
            that.tooltip.hide(d)
          })

        el.append('line')
          .attr('x1', 0)
          .attr('y1', 4)
          .attr('x2', 0)
          .attr('y2', 26)
          .style('stroke-width', 1)
          .style('stroke', that.sessionLegendColors[10].alt1)
          .style('fill', 'none')
      })

      this.lines = document.getElementsByClassName('battery-line')

      //Adding mouse over behavior
      var mouseG = this.chart.svg.append('g').attr('class', 'mouse-over-effects')

      mouseG
        .append('path') // this is the black vertical line to follow mouse
        .attr('class', 'mouse-line')
        .style('stroke', 'white')
        .style('stroke-width', '1px')
        .style('height', 30)
        .style('opacity', '0')

      //Adding overlay rect to listen about mouse over moves
      mouseG
        .append('rect')
        .attr('width', '100%')
        .attr('height', 30)
        .attr('fill', 'none')
        .attr('pointer-events', 'all')
        .on('mouseout', function() {
          // on mouse out hide line, circles and text
          d3.selectAll('.mouse-line').style('opacity', '0')
          d3.selectAll('.mouse-per-line-level circle').style('opacity', '0')
          d3.selectAll('.mouse-per-line-level text').style('opacity', '0')
          d3.selectAll('.mouse-per-line-temperature circle').style('opacity', '0')
          d3.selectAll('.mouse-per-line-temperature text').style('opacity', '0')
        })
        .on('mouseover', function() {
          // on mouse in show line, circles and text
          d3.selectAll('.mouse-line').style('opacity', '1')
          d3.selectAll('.mouse-per-line-level circle').style('opacity', '1')
          d3.selectAll('.mouse-per-line-level text').style('opacity', '1')
          d3.selectAll('.mouse-per-line-temperature circle').style('opacity', '1')
          d3.selectAll('.mouse-per-line-temperature text').style('opacity', '1')
        })
        .on('mousemove', function() {
          // mouse moving over canvas
          var mouse = d3.mouse(this)
          d3.selectAll('.mouse-line').attr('d', function() {
            var d = 'M' + mouse[0] + ',' + that.chart.width
            d += ' ' + mouse[0] + ',' + 0
            return d
          })

          that.batteryCharts.forEach(bchart => {
            // eslint-disable-next-line
            d3.selectAll(bchart.selector).attr('transform', function(d, i) {
              if (!that.lines[i] || i == bchart.index) {
                return
              }
              /*var xDate = bchart.xScale.invert(mouse[0])
              var bisect = d3.bisector(function(d) {
                return d.time
              }).right
              var idx = bisect(that.dataset.battery_levels, xDate)*/

              var beginning = 0,
                end = that.lines[i].getTotalLength()
              var target = null

              while (true) {
                target = Math.floor((beginning + end) / 2)

                var pos = that.lines[i].getPointAtLength(target)
                if ((target === end || target === beginning) && pos.x !== mouse[0]) {
                  break
                }
                if (pos.x > mouse[0]) end = target
                else if (pos.x < mouse[0]) beginning = target
                else break //position found
              }

              d3.select(this)
                .select('text')
                .text(bchart.yScale.invert(pos.y).toFixed(2))

              return 'translate(' + mouse[0] + ',' + pos.y + ')'
            })
          })
        })

      //Add legends
      let colors = this.sessionLegendColors.map(c => {
        return c.alt1
      })
      var color = d3
        .scaleOrdinal()
        .domain(this.sessionLegends)
        .range(colors)

      let legendRectSize = 10
      let legendSpacing = 5

      var legend = this.chart.svg
        .append('g')
        .selectAll('g')
        .data(color.domain())
        .enter()
        .append('g')
        .attr('class', 'legend')

      legend
        .append('rect')
        .attr('width', legendRectSize)
        .attr('height', legendRectSize)
        .attr('class', 'legendRect')
        .style('fill', color)
        .style('stroke', function() {
          return color
        })
        .style('opacity', '1.0')
        .attr('checked', true)
        .attr('id', function(d) {
          return 'legend-' + d
        })

      //Text of legends
      legend
        .append('text')
        .attr('x', legendRectSize + legendSpacing)
        .attr('y', legendSpacing + legendSpacing / 1.5)
        .text(function(d) {
          return d
        })
        .style('font-size', '10px')

      //We display legends dynamically by rows
      let currentX = 0
      let yLegendOffset = 1
      legend.attr('transform', function() {
        //let height = legendRectSize
        if (currentX > that.chart.width - that.chart.margin.left - that.chart.margin.right) {
          yLegendOffset += 1
          currentX = 0
        }

        let y = yOffset + yLegendOffset * that.chart.margin.bottom + 30
        let ret = 'translate(' + currentX + ',' + y + ')'
        currentX += this.getBBox().width + 2 * legendSpacing
        return ret
      })

      this.chart.svg
        .append('g')
        .selectAll('g')
        .data(color.domain())
        .enter()
        .append('g')
        .attr('class', 'legend')

      //let axisDateFormat = '%H:%M'

      this.chart.xAxis = d3
        .axisBottom(this.xScale)
        .ticks(20)
        .tickFormat(function(d) {
          return that.formatDuration(d)
        })

      this.timelineXAxis = this.chart.svg
        .append('g')
        .attr('class', 'chart-axis-timeline axis axis--x')
        .attr('transform', 'translate(0)')
        .call(this.chart.xAxis)

      this.chart.svgHeight = this.chart.svgHeight + yOffset
      d3.select('#svg-sessionTimelineChart').attr(
        'viewBox',
        '0 0 ' + this.chart.svgWidth + ' ' + this.chart.svgHeight,
      )
    },
    calculateBatteryPath: function(data, isBatteryLevel, batteryChartId) {
      let that = this

      var container = {
        index: isBatteryLevel ? 1 : 0,
        chart: null,
        xScale: null,
        yScale: null,
        valueline: null,
        selector: isBatteryLevel ? '.mouse-per-line-level' : '.mouse-per-line-temperature',
        idPathSelector: isBatteryLevel ? '#battery-levels-path' : '#battery-temperatures-path',
        idXAxisSelector: isBatteryLevel ? '#battery-levels-x-axis' : '#battery-temperatures-x-axis',
      }

      container.chart = new Chart(batteryChartId, data, 'session', this.width, 120, {
        top: 10,
        right: 20,
        bottom: 10,
        left: 50,
      })
      container.chart.initContainer(batteryChartId, 'session')

      container.xScale = d3
        .scaleLinear()
        .range([0, container.chart.width])
        .domain([this.dataset.start, this.dataset.end])

      container.yScale = d3
        .scaleLinear()
        .range([container.chart.height, 0])
        .domain([
          d3.min(data, function(d) {
            return d.level
          }),
          d3.max(data, function(d) {
            return d.level
          }),
        ])

      data.sort((a, b) => {
        return a.time - b.time
      })

      container.valueline = d3
        .line()
        .x(function(d) {
          return container.xScale(d.time)
        })
        .y(function(d) {
          return container.yScale(d.level)
        })

      //Adding zoom to chart to allow zoom by mouse scroll
      container.chart.svg.call(this.zoom)

      //Adding transparent rect to allow timeline grabbing
      container.chart.svg
        .append('rect')
        .attr('width', '100%')
        .attr('height', container.chart.height)
        .attr('fill', 'transparent')
        .attr('y', -30)
        .attr('style', 'cursor: grab;')
        .on('mousedown', function() {
          d3.selectAll('.tick')
            .selectAll('rect')
            .remove()
          d3.select(this).attr('style', 'cursor: grabbing;')
        })
        .on('mouseup', function() {
          d3.select(this).attr('style', 'cursor: grab;')
        })
        .on('mousemove', function() {
          d3.select(this).attr('style', 'cursor: grab;')
        })

      //Calculate gradients use to display a proper color according to the chart gradient / slope
      var gradientArray = new Array()
      var previousGradient

      container.chart.svg.append('defs')

      data.forEach((point, i, array) => {
        if (i + 1 < array.length) {
          var p1 = array[i]
          var p2 = array[i + 1]
          if (p2.time - p1.time != 0) {
            var gradient = ((p2.level - p1.level) / (p2.time - p1.time)) * 60
            var offset = (array[i].time * 100) / (array[array.length - 1].time - array[0].time)
            if (i > 0) {
              gradientArray.push({
                offset: offset - gradientArray[0].offsetValue + '%',
                color: this.gradientBatteryColor(
                  previousGradient,
                  isBatteryLevel ? 'battery_lifetime' : 'battery_temperature',
                ),
                gradient: gradient,
                offsetValue: offset,
              })
            }
            gradientArray.push({
              offset: '0%',
              color: this.gradientBatteryColor(
                gradient,
                isBatteryLevel ? 'battery_lifetime' : 'battery_temperature',
              ),
              gradient: gradient,
              offsetValue: offset,
            })
            previousGradient = gradient
          }
        }
      })

      //Linear gradient used to display battery chart
      container.chart.svg
        .select('defs')
        .append('linearGradient')
        .attr(
          'id',
          isBatteryLevel ? 'area-gradient-battery-levels' : 'area-gradient-battery-temperatures',
        )
        .attr('x1', gradientArray[0].offset)
        .attr('y1', '0%')
        .attr('x2', '100%')
        .attr('y2', '0%')
        .selectAll('stop')
        .data(gradientArray)
        .enter()
        .append('stop')
        .attr('offset', function(d) {
          return d.offset
        })
        .attr('stop-color', function(d) {
          return d.color
        })

      //Calculate path (line drawn) for the line chart
      container.chart.svg
        .append('path')
        .datum(data)
        .attr('class', 'battery-line')
        .style('stroke-width', '2px')
        .attr(
          'stroke',
          isBatteryLevel
            ? 'url(#area-gradient-battery-levels)'
            : 'url(#area-gradient-battery-temperatures)',
        )
        .attr('fill', 'none')
        .attr('id', isBatteryLevel ? 'battery-levels-path' : 'battery-temperatures-path')
        .attr('d', container.valueline)

      //Adding mouse over behavior
      var mouseG = container.chart.svg.append('g').attr('class', 'mouse-over-effects')

      mouseG
        .append('path') // this is the black vertical line to follow mouse
        .attr('class', 'mouse-line')
        .style('stroke', 'white')
        .style('stroke-width', '1px')
        .style('height', container.chart.height)
        .style('opacity', '0')

      this.lines = document.getElementsByClassName('battery-line')

      var mousePerLine = mouseG
        .selectAll(isBatteryLevel ? 'mouse-per-line-level' : 'mouse-per-line-temperature')
        .data(data)
        .enter()
        .append('g')
        .attr('class', isBatteryLevel ? 'mouse-per-line-level' : 'mouse-per-line-temperature')

      mousePerLine
        .append('circle')
        .attr('r', 5)
        .style('stroke', 'white')
        .style('fill', 'none')
        .style('stroke-width', '1px')
        .style('opacity', '0')

      mousePerLine
        .append('text')
        .attr('class', 'chart-title')
        .attr('transform', 'translate(10,3)')

      //Adding axis
      container.chart.xAxis = d3
        .axisBottom(container.xScale)
        .ticks(20)
        .tickFormat(function(d) {
          return that.formatDuration(d)
        })
        .tickSizeInner(-container.chart.height)
        .tickSizeOuter(3)
      container.chart.yAxis = d3
        .axisLeft(container.yScale)
        .tickSizeInner(-container.chart.width)
        .tickSizeOuter(3)

      // Add the X Axis
      container.chart.svg
        .append('g')
        .attr('transform', 'translate(0,' + container.chart.height + ')')
        .attr('class', 'chart-axis-timeline')
        .attr('id', isBatteryLevel ? 'battery-levels-x-axis' : 'battery-temperatures-x-axis')
        .call(container.chart.xAxis)

      // Add the Y Axis
      container.chart.svg
        .append('g')
        .attr('transform', 'translate(0, 0)')
        .attr('class', 'chart-axis-timeline')
        .call(container.chart.yAxis)

      // text label for the y axis
      container.chart.svg
        .append('text')
        .attr('id', 'y-axis-title')
        .attr('class', 'subtitle')
        .attr('transform', 'rotate(-90)')
        .attr('x', 0 - container.chart.height / 2)
        .attr('y', 0 - container.chart.margin.left)
        .attr('dy', '1em')
        .style('text-anchor', 'middle')
        .text(isBatteryLevel ? '(%)' : '(°C)')

      container.chart.svg
        .append('text')
        .attr('id', 'y-axis-title')
        .attr('class', 'subtitle')
        .attr('x', container.chart.width / 2)
        .attr('y', container.chart.height + container.chart.margin.bottom * 3 + 5)
        .style('text-anchor', 'middle')
        .text(this.$t('battery.time'))

      this.batteryCharts.push(container)

      d3.select('#svg-' + batteryChartId).attr(
        'viewBox',
        '0 0 ' + container.chart.svgWidth + ' 190',
      )

      //Adding overlay rect to listen about mouse over moves
      mouseG
        .append('rect')
        .attr('width', '100%')
        .attr('height', container.chart.height)
        .attr('fill', 'none')
        .attr('pointer-events', 'all')
        .on('mouseout', function() {
          // on mouse out hide line, circles and text
          d3.selectAll('.mouse-line').style('opacity', '0')
          d3.selectAll('.mouse-per-line-level circle').style('opacity', '0')
          d3.selectAll('.mouse-per-line-level text').style('opacity', '0')
          d3.selectAll('.mouse-per-line-temperature circle').style('opacity', '0')
          d3.selectAll('.mouse-per-line-temperature text').style('opacity', '0')
        })
        .on('mouseover', function() {
          // on mouse in show line, circles and text
          d3.selectAll('.mouse-line').style('opacity', '1')
          d3.selectAll('.mouse-per-line-level circle').style('opacity', '1')
          d3.selectAll('.mouse-per-line-level text').style('opacity', '1')
          d3.selectAll('.mouse-per-line-temperature circle').style('opacity', '1')
          d3.selectAll('.mouse-per-line-temperature text').style('opacity', '1')
        })
        .on('mousemove', function() {
          // mouse moving over canvas
          var mouse = d3.mouse(this)
          d3.selectAll('.mouse-line').attr('d', function() {
            var d = 'M' + mouse[0] + ',' + container.chart.width
            d += ' ' + mouse[0] + ',' + 0
            return d
          })

          that.batteryCharts.forEach(bchart => {
            // eslint-disable-next-line
            d3.selectAll(bchart.selector).attr('transform', function(d, i) {
              if (!that.lines[i] || i == bchart.index) {
                return
              }
              /*var xDate = bchart.xScale.invert(mouse[0])
              var bisect = d3.bisector(function(d) {
                return d.time
              }).right
              var idx = bisect(data, xDate)*/

              var beginning = 0,
                end = that.lines[i].getTotalLength()
              var target = null

              while (true) {
                target = Math.floor((beginning + end) / 2)

                var pos = that.lines[i].getPointAtLength(target)
                if ((target === end || target === beginning) && pos.x !== mouse[0]) {
                  break
                }
                if (pos.x > mouse[0]) end = target
                else if (pos.x < mouse[0]) beginning = target
                else break //position found
              }

              d3.select(this)
                .select('text')
                .text(bchart.yScale.invert(pos.y).toFixed(2))

              return 'translate(' + mouse[0] + ',' + pos.y + ')'
            })
          })
        })
    },
    downloadSession: function() {
      let link = document.getElementById('downloadAnchorElem')
      link.href = `data:text/json;charset=utf-8,${JSON.stringify(this.dataset.raw_log, null, 4)}`
      link.setAttribute('download', `Log_${this.dataset.raw_log.sessionID}.json`)
      link.click()
    },
    onMapShow: function() {
      this.displayMap = false
      setTimeout(() => {
        this.displayMap = true
        this.loadMapData()
      }, 500)
    },
    getTimeLabel: function(timeKey) {
      return moment(this.dataset[timeKey]).format('DD/MM/YYYY HH:mm')
    },
    isSurveyOriginQuestion: function(question) {
      return (
        question === 'Survey_Origin_Question' ||
        question === 'Survey_Origin_Pays' ||
        question === 'Survey_Origin_Departement' ||
        question === 'Survey_Origin_France'
      )
    },
    loadMapData: function() {
      this.dataset.routes.forEach(route => {
        route['value'] = route[this.routeForm.dataType]
      })
      this.dataset = JSON.parse(JSON.stringify(this.dataset))
      this.routeForm = JSON.parse(JSON.stringify(this.routeForm))
    },
    formatDuration(duration) {
      return moment.utc(moment.duration(duration, 'seconds').asMilliseconds()).format('HH:mm')
    },
    getTooltipLabel(item) {
      const tooltipLabel = ''
      let areaName = item.area
      if (that.dataset.raw_log.logVersionMajor >= 4) {
        areaName =
          that.$i18n.messages[that.$i18n.locale]['map_areas'][that.dataset.raw_log.siteID][
            item.area
          ]
      }

      const time = moment.utc(item.start * 1000).format('HH:mm:ss')

      tooltipLabel += time + ' : '

      if (item.type === 'Beacon' || item.type === 'Scan') {
        tooltipLabel += this.$t(`chart.data_value.${item.type.toLowerCase()}`) + ' - '
      }

      if (item.type === 'Warning' || item.type === 'Beacon') {
        tooltipLabel += `<strong>${item.name}</strong> - ${areaName}`
      } else if (item.type === 'Area') {
        tooltipLabel += `<strong>${areaName}</strong>`
      } else {
        tooltipLabel += `<strong>${d.name}</strong>`
      }

      return tooltipLabel
    },
    onOverLogEvent(time) {
      let that = this
      d3.selectAll('.mouse-line').style('opacity', '1')
      d3.selectAll('.mouse-per-line-level circle').style('opacity', '1')
      d3.selectAll('.mouse-per-line-level text').style('opacity', '1')
      d3.selectAll('.mouse-per-line-temperature circle').style('opacity', '1')
      d3.selectAll('.mouse-per-line-temperature text').style('opacity', '1')

      const duration = Math.round(
        moment.duration(moment(time).diff(moment(this.dataset.start_time))).asSeconds(),
      )

      const value = this.xReScale(duration)

      d3.selectAll('.mouse-line').attr('d', function() {
        let d = 'M' + value + ',' + that.chart.width
        d += ' ' + value + ',' + 0
        return d
      })
    },
    onOutLogEvent() {
      d3.selectAll('.mouse-line').style('opacity', '0')
      d3.selectAll('.mouse-per-line-level circle').style('opacity', '0')
      d3.selectAll('.mouse-per-line-level text').style('opacity', '0')
      d3.selectAll('.mouse-per-line-temperature circle').style('opacity', '0')
      d3.selectAll('.mouse-per-line-temperature text').style('opacity', '0')
    },
    eventTime(time) {
      return moment(time).format('HH:mm:ss')
    },
    areaName(area) {
      if (this.$i18n.messages[this.$i18n.locale]['map_areas'][this.dataset.raw_log.siteID]) {
        return this.$i18n.messages[this.$i18n.locale]['map_areas'][this.dataset.raw_log.siteID][
          area
        ]
      } else {
        return ''
      }
    },
    onEventClick(event) {
      this.clickedLogEvent = event
      if (this.logReaderCollapsed) {
        $('#collapseLogReader').collapse('show')
      } else {
        this.selectedLogEvent = this.clickedLogEvent
      }
    },
  },
}
</script>

<style>
.header-title {
  padding: 1rem 1.5rem 0rem 1.5rem;
  text-align: center;
}

.line-session {
  fill: none;
  stroke: steelblue;
  stroke-width: 2px;
}

.survey-indicator {
  padding: 1rem 1.5rem;
  text-align: center;
}

/* Color used for unused map zones */
.cls-1 {
  fill: #0b3d5f;
}

.cls-2 {
  fill: #a3bfce;
}

/* Map */
.canvas {
  overflow: hidden;
}

.canvas .wrapper.outer > .background-map {
  fill: #000000;
}

.canvas .wrapper.inner > .background-map {
  fill: #2e4a57;
  cursor: move;
}

.canvas .background-map {
  /*fill: #F6F6F6;*/
  fill: #2e4a57;
  /*stroke: #333333;*/
  cursor: move;
}

.canvas .panCanvas {
  cursor: move;
}

.canvas .minimap .frame {
  pointer-events: all;
}
.canvas .minimap .frame .background-map {
  stroke: #111111;
  stroke-width: 4px;
  fill-opacity: 0.1;
  fill: #000000;
  fill: url(#minimapGradient_qwpyza);
  filter: url(#minimapDropShadow_qwpyza);
  cursor: move;
}

.overlay {
  position: fixed;
  display: block;
  width: 100%;
  height: 100%;
  top: 100;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #2e4a57;
  z-index: 500;
  cursor: -webkit-grab;
}

.btn-overlay {
  position: fixed;
  width: auto;
  height: auto;
  bottom: 0.5rem;
  right: 2rem;
  z-index: 600;
}

.btn-overlay button {
  background-color: #212529;
}

.session .tick line {
  opacity: 0.2;
}
</style>
